// /projects/new — the brief-paste-to-matched-grants flow.
//
// Two modes, switchable from Step 1:
//   - brief (default): paste → extract → confirm → match → save
//   - manual: single-page form, no extraction, no citations, then
//     optional inline matching
//
// Demo bar: this is the screen the CEO writes the check after watching.
// Apply the design principles strictly. Citation disclosure is delightful
// (verbatim quote in italic-indent-accent-border). Loading is bounded
// (~3-5s for extraction, ~5s for matching). Empty match result is
// conversational (not "0 matches").
//
// Per Phase 1 §I.4 (modification #4): the partner_id field in Step 3 gets
// amber-tinted treatment. partner_rationale is visible by default.
// "Find matching grants" stays disabled until Lisa explicitly interacts
// with the partner field (click confirm or change selection).
//
// Per §I.5 (modification #5): manual mode = same form, no extraction,
// single page. Submit → optional "Find matching grants now?" → redirect.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { formatDate, projectStatusMeta } from '../lib/format';
import { pushToast } from '../hooks/useToasts';
import { PageFooter } from '../components/PageFooter';

const MIN_CHARS = 50;
const MAX_CHARS = 10_000;

type Mode = 'brief' | 'manual';
type Step = 1 | 2 | 3 | 4 | 5;

interface Partner { id: string; name: string; mandate: string | null; slug?: string }

interface SourceQuotes {
  name: string | null;
  description: string | null;
  ask_cad: string | null;
  match_available_cad: string | null;
  target_funding_date: string | null;
  partner_id: string | null;
}

interface BriefExtraction {
  name: string;
  description: string | null;
  ask_cad: number | null;
  match_available_cad: number | null;
  target_funding_date: number | null;
  partner_id: string | null;
  partner_rationale: string;
  source_quotes: SourceQuotes;
  notes: string;
}

interface MatchCard {
  id: string;
  grant_program_id: string;
  fit_score: number;
  eligibility_verdict: string;
  status: string;
  grant_title: string;
  grant_source_url: string;
  grant_closes_at: number | null;
  funder_name: string;
  funder_level: string;
  funding_min_cad: number | null;
  funding_max_cad: number | null;
  intake_type: string | null;
}

interface ProjectMatchRunResult {
  project_id: string;
  candidates_after_hard_filter: number;
  verdicts_attempted: number;
  matches_inserted: number;
  matches_updated: number;
  errors: number;
}

export default function ProjectNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('brief');
  const [step, setStep] = useState<Step>(1);
  const [brief, setBrief] = useState('');
  const [extraction, setExtraction] = useState<BriefExtraction | null>(null);
  const [partnerInteracted, setPartnerInteracted] = useState(false);
  const [savedProject, setSavedProject] = useState<{ id: string; name: string } | null>(null);
  const [pursuedMatchIds, setPursuedMatchIds] = useState<string[]>([]);

  const partners = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get<Partner[]>('/partners'),
  });

  // ── Extract from brief ────────────────────────────────────────────────

  const extractMut = useMutation({
    mutationFn: () =>
      api.post<BriefExtraction>('/projects/from-brief', { brief }),
    onMutate: () => { setStep(2); },
    onSuccess: (res) => {
      setExtraction(res.data);
      // Reset interaction gate: a fresh extraction means Lisa hasn't
      // confirmed the partner yet (mod #4).
      setPartnerInteracted(false);
      setStep(3);
    },
    onError: (err) => {
      pushToast(
        err instanceof ApiError ? err.message : 'Extraction failed',
        'error',
      );
      setStep(1);
    },
  });

  // ── Save project ──────────────────────────────────────────────────────

  const saveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ id: string; name: string; status: string }>('/projects', body),
    onError: (err) => {
      pushToast(err instanceof ApiError ? err.message : 'Save failed', 'error');
    },
  });

  // ── Run matching ──────────────────────────────────────────────────────

  const matchMut = useMutation({
    mutationFn: (projectId: string) =>
      api.post<ProjectMatchRunResult>(`/projects/${projectId}/match`),
    onMutate: () => { setStep(4); },
    onSuccess: () => { setStep(5); },
    onError: (err) => {
      pushToast(err instanceof ApiError ? err.message : 'Matching failed', 'error');
      setStep(3);
    },
  });

  // ── Matches list (refreshed after match run) ──────────────────────────

  const matchesQ = useQuery({
    queryKey: ['project-matches', savedProject?.id],
    queryFn: async () => {
      if (!savedProject) return null;
      const detail = await api.get<{ matches: MatchCard[] }>(`/projects/${savedProject.id}`);
      return detail.data.matches;
    },
    enabled: !!savedProject && step === 5,
  });

  // ── Pursue / dismiss action ────────────────────────────────────────────

  const updateMatchMut = useMutation({
    mutationFn: ({ matchId, status }: { matchId: string; status: 'pursuing' | 'dismissed' | 'watching' }) =>
      api.patch(`/project-matches/${matchId}`, { status }),
    onError: (err) => {
      pushToast(err instanceof ApiError ? err.message : 'Status change failed', 'error');
    },
  });

  // ── Global ⌘⏎ advance ─────────────────────────────────────────────────

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') return;
      e.preventDefault();
      handleAdvance();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, step, brief, extraction, partnerInteracted, savedProject]);

  function handleAdvance() {
    if (mode === 'brief') {
      if (step === 1 && brief.trim().length >= MIN_CHARS) extractMut.mutate();
      else if (step === 3 && extraction && partnerInteracted && !saveMut.isPending) handleSaveAndMatch();
      else if (step === 5 && savedProject) handleFinish();
    } else {
      if (step === 3 && extraction && !saveMut.isPending) handleSaveAndMatch();
      else if (step === 5 && savedProject) handleFinish();
    }
  }

  async function handleSaveAndMatch() {
    if (!extraction) return;
    const body: Record<string, unknown> = {
      partner_profile_id: extraction.partner_id,
      name: extraction.name,
      description: extraction.description,
      ask_cad: extraction.ask_cad,
      match_available_cad: extraction.match_available_cad,
      target_funding_date: extraction.target_funding_date,
      status: 'planning',
    };
    if (mode === 'brief' && brief) body.brief_text = brief;
    if (mode === 'brief' && extraction.source_quotes) body.source_quotes = extraction.source_quotes;

    const res = await saveMut.mutateAsync(body);
    setSavedProject({ id: res.data.id, name: res.data.name });
    matchMut.mutate(res.data.id);
  }

  async function handleSaveManualOnly() {
    if (!extraction) return;
    const body: Record<string, unknown> = {
      partner_profile_id: extraction.partner_id,
      name: extraction.name,
      description: extraction.description,
      ask_cad: extraction.ask_cad,
      match_available_cad: extraction.match_available_cad,
      target_funding_date: extraction.target_funding_date,
      status: 'planning',
    };
    const res = await saveMut.mutateAsync(body);
    qc.invalidateQueries({ queryKey: ['projects'] });
    pushToast(`Saved ${res.data.name}`, 'success', 1800);
    navigate({ to: '/projects/$id', params: { id: res.data.id } });
  }

  function handleFinish() {
    if (!savedProject) return;
    // Pursue selected matches (set status='pursuing'). Best-effort — toast
    // any errors but proceed; the project is already saved.
    Promise.all(
      pursuedMatchIds.map((mid) =>
        updateMatchMut.mutateAsync({ matchId: mid, status: 'pursuing' }).catch(() => null),
      ),
    ).then(() => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      pushToast(
        pursuedMatchIds.length
          ? `Saved with ${pursuedMatchIds.length} match${pursuedMatchIds.length === 1 ? '' : 'es'} pursued`
          : 'Saved',
        'success',
        2000,
      );
      navigate({ to: '/projects/$id', params: { id: savedProject.id } });
    });
  }

  function switchToManualMode() {
    setMode('manual');
    setStep(3);
    setExtraction({
      name: '',
      description: null,
      ask_cad: null,
      match_available_cad: null,
      target_funding_date: null,
      partner_id: null,
      partner_rationale: '',
      source_quotes: {
        name: null, description: null, ask_cad: null,
        match_available_cad: null, target_funding_date: null, partner_id: null,
      },
      notes: '',
    });
    setPartnerInteracted(true); // manual mode = no partner inference, no gate
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[760px] mx-auto px-5 py-5">
          <BreadcrumbAndExit />
          <ProgressStrip mode={mode} step={step} />

          {/* STEP 1 — Paste brief */}
          {mode === 'brief' && step === 1 && (
            <Step1Paste
              brief={brief}
              setBrief={setBrief}
              canExtract={brief.trim().length >= MIN_CHARS}
              onExtract={() => extractMut.mutate()}
              onSwitchManual={switchToManualMode}
              isPending={extractMut.isPending}
            />
          )}

          {/* STEP 2 — Loading extraction */}
          {mode === 'brief' && step === 2 && <Step2ExtractLoading />}

          {/* STEP 3 — Confirm fields (or manual entry) */}
          {step === 3 && extraction && (
            <Step3Confirm
              mode={mode}
              extraction={extraction}
              setExtraction={setExtraction}
              partners={partners.data?.data ?? []}
              partnerInteracted={partnerInteracted}
              setPartnerInteracted={setPartnerInteracted}
              onSaveAndMatch={handleSaveAndMatch}
              onSaveOnly={handleSaveManualOnly}
              onRestart={() => {
                setBrief('');
                setExtraction(null);
                setMode('brief');
                setStep(1);
              }}
              isSaving={saveMut.isPending}
            />
          )}

          {/* STEP 4 — Loading matches */}
          {step === 4 && <Step4MatchingLoading />}

          {/* STEP 5 — Results */}
          {step === 5 && savedProject && (
            <Step5Results
              savedProject={savedProject}
              extraction={extraction}
              matches={matchesQ.data ?? []}
              isLoading={matchesQ.isLoading}
              pursuedMatchIds={pursuedMatchIds}
              setPursuedMatchIds={setPursuedMatchIds}
              onFinish={handleFinish}
              isFinishing={updateMatchMut.isPending}
            />
          )}
        </div>
      </div>

      <PageFooter />
    </div>
  );
}

// ── Header + Progress ────────────────────────────────────────────────────

function BreadcrumbAndExit() {
  return (
    <header className="flex items-center justify-between mb-4">
      <nav aria-label="Breadcrumb" className="text-xs text-neutral-500">
        <Link to="/projects" className="hover:text-neutral-700 underline-offset-2 hover:underline">Projects</Link>
        <span className="mx-1">›</span>
        <span className="text-neutral-900">New project</span>
      </nav>
      <Link
        to="/projects"
        className="text-xs text-neutral-600 rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
        aria-label="Cancel and return to projects list"
      >Cancel <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">Esc</kbd></Link>
    </header>
  );
}

function ProgressStrip({ mode, step }: { mode: Mode; step: Step }) {
  const steps =
    mode === 'manual'
      ? [{ n: 3 as Step, label: 'Details' }, { n: 5 as Step, label: 'Save' }]
      : [
          { n: 1 as Step, label: 'Brief' },
          { n: 3 as Step, label: 'Confirm' },
          { n: 5 as Step, label: 'Match' },
        ];
  return (
    <ol className="mb-6 flex items-center gap-2 text-xs text-neutral-500" aria-label="Progress">
      {steps.map((s, i) => {
        const isCurrent = (s.n === 1 && step <= 2) || (s.n === 3 && step >= 3 && step <= 4) || (s.n === 5 && step === 5);
        const isDone =
          (s.n === 1 && step >= 3) ||
          (s.n === 3 && step >= 5);
        return (
          <li key={s.n} className="flex items-center gap-2">
            <span className={
              'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-medium ' +
              (isCurrent ? 'bg-accent text-white' : isDone ? 'bg-emerald-200 text-emerald-900' : 'bg-neutral-200 text-neutral-700')
            }>{i + 1}</span>
            <span className={isCurrent ? 'text-neutral-900 font-medium' : ''}>{s.label}</span>
            {i < steps.length - 1 && <span className="text-neutral-300 mx-1">—</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ── Step 1 ────────────────────────────────────────────────────────────────

function Step1Paste({
  brief, setBrief, canExtract, onExtract, onSwitchManual, isPending,
}: {
  brief: string;
  setBrief: (s: string) => void;
  canExtract: boolean;
  onExtract: () => void;
  onSwitchManual: () => void;
  isPending: boolean;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { taRef.current?.focus(); }, []);
  return (
    <section>
      <h1 className="text-xl font-semibold text-neutral-900 mb-1">Paste a project brief</h1>
      <p className="text-sm text-neutral-600 mb-4 max-w-prose">
        Paste anything from a paragraph to a full multi-page document — an email from a program director, a strategy memo, a one-pager. Granted will extract the project's name, scope, budget, and timeline, then find matching grants. The extracted details are yours to review and edit before saving.
      </p>

      <label className="block">
        <span className="sr-only">Project brief</span>
        <textarea
          ref={taRef}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={14}
          maxLength={MAX_CHARS}
          placeholder="Paste the brief here…"
          className="w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm leading-relaxed focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent"
        />
      </label>
      <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
        <span>{brief.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters</span>
        {!canExtract && brief.length > 0 && (
          <span>At least {MIN_CHARS} characters needed.</span>
        )}
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onExtract}
          disabled={!canExtract || isPending}
          className="inline-flex items-center gap-2 bg-accent text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-accent-hover disabled:opacity-50"
        >
          Extract project details
          <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⌘⏎</kbd>
        </button>
        <button
          type="button"
          onClick={onSwitchManual}
          className="text-sm text-accent underline"
        >
          Create project manually instead →
        </button>
      </div>
      <p className="mt-4 text-[11px] text-neutral-500 max-w-prose">
        Granted only matches against verified grants. Brief contents are sent to Anthropic via the AI Gateway with all configured staff and partner names redacted. The extracted JSON is stored alongside the project; the raw brief text is stored verbatim for your own reference.
      </p>
    </section>
  );
}

// ── Step 2 ────────────────────────────────────────────────────────────────

function Step2ExtractLoading() {
  return (
    <section aria-live="polite">
      <h1 className="text-xl font-semibold text-neutral-900 mb-1">
        Extracting project details
      </h1>
      <p className="text-sm text-neutral-600 mb-4">analyzing structure… typically 3–5 seconds</p>
      <div className="space-y-2">
        {['Project name', 'Description', 'Ask amount', 'Match available', 'Target date', 'Partner'].map((label) => (
          <div key={label} className="border border-neutral-200 rounded-md p-3 bg-neutral-50/40 animate-pulse">
            <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-1">{label}</div>
            <div className="h-3 bg-neutral-200 rounded w-3/4" />
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Step 3 ────────────────────────────────────────────────────────────────

function Step3Confirm({
  mode,
  extraction,
  setExtraction,
  partners,
  partnerInteracted,
  setPartnerInteracted,
  onSaveAndMatch,
  onSaveOnly,
  onRestart,
  isSaving,
}: {
  mode: Mode;
  extraction: BriefExtraction;
  setExtraction: (e: BriefExtraction) => void;
  partners: Partner[];
  partnerInteracted: boolean;
  setPartnerInteracted: (v: boolean) => void;
  onSaveAndMatch: () => void;
  onSaveOnly: () => void;
  onRestart: () => void;
  isSaving: boolean;
}) {
  const update = <K extends keyof BriefExtraction>(k: K, v: BriefExtraction[K]) => {
    setExtraction({ ...extraction, [k]: v });
  };

  const canContinue = (mode === 'manual' || partnerInteracted) && !!extraction.name.trim() && !!extraction.partner_id;

  return (
    <section>
      <h1 className="text-xl font-semibold text-neutral-900 mb-1">
        {mode === 'brief' ? 'Confirm the extracted details' : 'Enter project details'}
      </h1>
      <p className="text-sm text-neutral-600 mb-4 max-w-prose">
        {mode === 'brief'
          ? 'Granted pulled these fields from the brief. Edit any field; the verbatim source quote sits beside each one. Required fields: name and partner.'
          : 'Fill in what you know. Required: name and partner. The rest can be added later.'}
      </p>

      <div className="space-y-4">
        <FieldRow
          label="Project name"
          quote={mode === 'brief' ? extraction.source_quotes.name : null}
          required
        >
          <input
            type="text"
            value={extraction.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="A short, specific name"
            className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
          />
        </FieldRow>

        <FieldRow
          label="Description"
          quote={mode === 'brief' ? extraction.source_quotes.description : null}
          hint="1–3 sentences. Drives the matching engine."
        >
          <textarea
            value={extraction.description ?? ''}
            onChange={(e) => update('description', e.target.value || null)}
            rows={3}
            placeholder="What this project does, in plain language"
            className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm leading-relaxed"
          />
        </FieldRow>

        <FieldRow
          label="Ask amount (CAD)"
          quote={mode === 'brief' ? extraction.source_quotes.ask_cad : null}
        >
          <div className="flex items-center gap-1">
            <span className="text-neutral-500 text-sm">$</span>
            <input
              type="number"
              min={0}
              value={extraction.ask_cad ?? ''}
              onChange={(e) => update('ask_cad', e.target.value === '' ? null : Number(e.target.value))}
              className="flex-1 border border-neutral-300 rounded-md px-2 py-1.5 text-sm tabular-nums"
              placeholder="leave blank if unknown"
            />
          </div>
        </FieldRow>

        <FieldRow
          label="Match available (CAD)"
          quote={mode === 'brief' ? extraction.source_quotes.match_available_cad : null}
          hint="Cash this partner can commit toward matching funds for this project."
        >
          <div className="flex items-center gap-1">
            <span className="text-neutral-500 text-sm">$</span>
            <input
              type="number"
              min={0}
              value={extraction.match_available_cad ?? ''}
              onChange={(e) => update('match_available_cad', e.target.value === '' ? null : Number(e.target.value))}
              className="flex-1 border border-neutral-300 rounded-md px-2 py-1.5 text-sm tabular-nums"
              placeholder="leave blank if unknown"
            />
          </div>
        </FieldRow>

        <FieldRow
          label="Target funding date"
          quote={mode === 'brief' ? extraction.source_quotes.target_funding_date : null}
          hint="When the project needs the funding in hand."
        >
          <input
            type="date"
            value={extraction.target_funding_date ? new Date(extraction.target_funding_date).toISOString().slice(0, 10) : ''}
            onChange={(e) => {
              if (!e.target.value) return update('target_funding_date', null);
              const [y, m, d] = e.target.value.split('-').map(Number);
              update('target_funding_date', Date.UTC(y, m - 1, d, 23, 59, 59));
            }}
            className="border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
          />
          {extraction.target_funding_date && (
            <span className="ml-2 text-[11px] text-neutral-500">{formatDate(extraction.target_funding_date)}</span>
          )}
        </FieldRow>

        {/* Partner — amber-tinted treatment per mod #4 */}
        <PartnerField
          mode={mode}
          value={extraction.partner_id}
          partners={partners}
          rationale={extraction.partner_rationale}
          partnerInteracted={partnerInteracted}
          onChange={(id) => {
            update('partner_id', id);
            setPartnerInteracted(true);
          }}
          onConfirm={() => setPartnerInteracted(true)}
        />

        {extraction.notes && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <span className="font-medium">Extraction note:</span> {extraction.notes}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center flex-wrap gap-3">
        <button
          type="button"
          onClick={onSaveAndMatch}
          disabled={!canContinue || isSaving}
          className="inline-flex items-center gap-2 bg-accent text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-accent-hover disabled:opacity-50"
          title={!canContinue && !partnerInteracted ? 'Confirm or change the partner before continuing.' : undefined}
        >
          {isSaving ? 'Saving…' : 'Find matching grants →'}
          <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⌘⏎</kbd>
        </button>
        {mode === 'manual' && (
          <button
            type="button"
            onClick={onSaveOnly}
            disabled={!canContinue || isSaving}
            className="text-sm rounded-md border border-neutral-300 px-3 py-1.5 hover:bg-neutral-50 disabled:opacity-50"
          >Save project, skip matching for now</button>
        )}
        {mode === 'brief' && (
          <button
            type="button"
            onClick={onRestart}
            className="text-sm text-neutral-600 underline"
          >Start over with a new brief</button>
        )}
      </div>
      {!partnerInteracted && mode === 'brief' && (
        <p className="mt-2 text-[11px] text-amber-900">Confirm or change the partner before continuing.</p>
      )}
    </section>
  );
}

function FieldRow({
  label, required, hint, quote, children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  quote?: string | null;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(quote != null);
  return (
    <div className="rounded-md border border-neutral-200 p-3 bg-white">
      <label className="block">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <span className="text-xs text-neutral-700">
            {label}{required && <span className="text-rose-700 ml-1">*</span>}
          </span>
          {quote && (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="text-[10px] text-neutral-500 hover:text-neutral-700 underline-offset-2 hover:underline"
              aria-expanded={open}
            >
              {open ? 'Hide source quote' : 'Show source quote'}
            </button>
          )}
        </div>
        {children}
      </label>
      {hint && <p className="mt-1 text-[11px] text-neutral-500">{hint}</p>}
      {quote && open && (
        <blockquote className="mt-2 border-l-2 border-accent pl-3 italic text-xs text-neutral-700 leading-relaxed">
          {quote}
        </blockquote>
      )}
      {!quote && quote === null && (
        <p className="mt-1 text-[11px] text-amber-900 italic">No verbatim source — review manually before saving.</p>
      )}
    </div>
  );
}

function PartnerField({
  mode, value, partners, rationale, partnerInteracted, onChange, onConfirm,
}: {
  mode: Mode;
  value: string | null;
  partners: Partner[];
  rationale: string;
  partnerInteracted: boolean;
  onChange: (id: string | null) => void;
  onConfirm: () => void;
}) {
  // Amber treatment when mode='brief' AND lisa hasn't confirmed yet.
  const needsConfirm = mode === 'brief' && !partnerInteracted;
  return (
    <div
      className={
        'rounded-md p-3 border ' +
        (needsConfirm ? 'border-amber-300 bg-amber-50' : 'border-neutral-200 bg-white')
      }
    >
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <label className="text-xs text-neutral-800 font-medium">
          Partner <span className="text-rose-700 ml-1">*</span>
        </label>
        {needsConfirm && (
          <span className="text-[10px] uppercase tracking-wide text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded">
            Needs confirmation
          </span>
        )}
      </div>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm bg-white"
      >
        <option value="">— Choose a partner —</option>
        {partners.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      {mode === 'brief' && rationale && (
        <p className="mt-2 text-[11px] text-amber-950 leading-relaxed">
          <span className="font-medium">Why this partner: </span>{rationale}
        </p>
      )}
      {needsConfirm && (
        <div className="mt-2 flex items-center gap-3">
          <span className="text-[11px] text-amber-900">
            This is Granted's best guess based on the brief. Confirm or change the partner before continuing.
          </span>
          <button
            type="button"
            onClick={onConfirm}
            className="ml-auto text-xs rounded-md bg-amber-200 text-amber-950 px-2 py-1 hover:bg-amber-300 shrink-0"
          >Confirm partner</button>
        </div>
      )}
    </div>
  );
}

// ── Step 4 ────────────────────────────────────────────────────────────────

function Step4MatchingLoading() {
  return (
    <section aria-live="polite">
      <h1 className="text-xl font-semibold text-neutral-900 mb-1">Searching verified grants</h1>
      <p className="text-sm text-neutral-600 mb-4">about 5 seconds…</p>
      <ul className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <li key={i} className="border border-neutral-200 rounded-md p-3 bg-neutral-50/40 animate-pulse">
            <div className="h-3 bg-neutral-200 rounded w-3/5 mb-1.5" />
            <div className="h-2 bg-neutral-200 rounded w-2/5 mb-1.5" />
            <div className="h-1.5 bg-neutral-200 rounded w-1/3" />
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Step 5 ────────────────────────────────────────────────────────────────

function Step5Results({
  savedProject,
  extraction,
  matches,
  isLoading,
  pursuedMatchIds,
  setPursuedMatchIds,
  onFinish,
  isFinishing,
}: {
  savedProject: { id: string; name: string };
  extraction: BriefExtraction | null;
  matches: MatchCard[];
  isLoading: boolean;
  pursuedMatchIds: string[];
  setPursuedMatchIds: (ids: string[]) => void;
  onFinish: () => void;
  isFinishing: boolean;
}) {
  const planning = projectStatusMeta.planning;
  const toggle = (id: string) => {
    setPursuedMatchIds(pursuedMatchIds.includes(id) ? pursuedMatchIds.filter((x) => x !== id) : [...pursuedMatchIds, id]);
  };
  return (
    <section>
      <header className="rounded-md border border-neutral-200 bg-neutral-50/40 px-4 py-3 mb-5">
        <div className="flex items-baseline gap-2">
          <h1 className="text-base font-semibold text-neutral-900">{savedProject.name}</h1>
          <span className={'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + planning.cls}>{planning.label}</span>
        </div>
        {extraction && (
          <p className="mt-1 text-xs text-neutral-600">
            Ask {extraction.ask_cad ? `$${extraction.ask_cad.toLocaleString('en-CA')}` : '—'} ·
            Committed $0 ·
            {extraction.target_funding_date ? ` Target ${formatDate(extraction.target_funding_date)}` : ' No target date'}
          </p>
        )}
      </header>

      <div className="flex items-start justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-neutral-900">
          Matching grants <span className="text-neutral-500 font-normal">({matches.length})</span>
        </h2>
        <button
          type="button"
          onClick={onFinish}
          disabled={isFinishing}
          className="inline-flex items-center gap-2 bg-accent text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-accent-hover disabled:opacity-50"
        >
          {isFinishing ? 'Saving…' : 'Save project'}
          <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⌘⏎</kbd>
        </button>
      </div>

      {isLoading && <p className="text-sm text-neutral-600">Loading matches — a moment.</p>}

      {!isLoading && matches.length === 0 && (
        <div className="rounded-md border border-neutral-200 bg-neutral-50/40 p-4 text-sm">
          <p className="font-medium text-neutral-900">No matching verified grants yet.</p>
          <p className="mt-1 text-xs text-neutral-700 leading-relaxed max-w-prose">
            Granted only matches against grants a human has approved in <Link to="/admin/verify" className="underline text-accent">Verification</Link>. If the project description is broad, try editing it to be more specific and run matching again from the project page after saving — or wait for the overnight cron to surface new candidates.
          </p>
        </div>
      )}

      {!isLoading && matches.length > 0 && (
        <ul className="space-y-2">
          {matches.map((m) => (
            <MatchResultCard
              key={m.id}
              match={m}
              pursued={pursuedMatchIds.includes(m.id)}
              onTogglePursue={() => toggle(m.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function MatchResultCard({
  match, pursued, onTogglePursue,
}: {
  match: MatchCard;
  pursued: boolean;
  onTogglePursue: () => void;
}) {
  const days = match.grant_closes_at
    ? Math.round((match.grant_closes_at - Date.now()) / 86_400_000)
    : null;
  const closesTone = days == null ? 'neutral' : days < 7 ? 'red' : days < 14 ? 'amber' : 'neutral';
  return (
    <li className={
      'border rounded-md p-3 bg-white ' +
      (pursued ? 'border-accent bg-accent/5' : 'border-neutral-200')
    }>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-neutral-900">{match.grant_title}</h3>
          <p className="text-xs text-neutral-600 mt-0.5">
            {match.funder_name} · <span className="uppercase tracking-wide">{match.funder_level}</span>
            {days != null && (
              <span className={
                'ml-2 text-[10px] px-1.5 py-0.5 rounded ' +
                (closesTone === 'red' ? 'bg-rose-100 text-rose-900' :
                 closesTone === 'amber' ? 'bg-amber-100 text-amber-900' :
                 'bg-neutral-100 text-neutral-700')
              }>
                Closes in {days} day{days === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        <div className="shrink-0 text-right text-xs">
          <div className="font-medium text-neutral-900">{Math.round(match.fit_score * 100)}% fit</div>
          <div className="text-neutral-600 mt-0.5">{match.eligibility_verdict.replace('_', ' ')}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={onTogglePursue}
          className={
            'rounded-md px-2 py-1 ' +
            (pursued
              ? 'bg-accent text-white'
              : 'border border-neutral-300 text-neutral-800 hover:bg-neutral-50')
          }
          aria-pressed={pursued}
        >
          {pursued ? '✓ Pursuing' : 'Pursue (add to project)'}
        </button>
        <a
          href={match.grant_source_url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent underline"
        >Funder page ↗</a>
      </div>
    </li>
  );
}
